import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import {
  mapIntegrationLinksListFromDTO,
  mergeIntegrations,
} from '@/modules/common/mappers/integration.mapper';
import type {
  IntegrationInfoResponse,
  IntegrationLinksResponse,
} from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { axiosInstance } from '@/utils/axios';

import { mapEntityIntegrationsToTeamMemberIntegrations } from '../mappers/team-member-integration.mapper';
import { TeamMemberIntegration } from '../types/team-member-integration';

/**
 * Retrieves all available integrations with their unlinked external team members.
 * Optionally merges with selected integrations from team member details.
 *
 * @param selectedIntegrations - Optional integration data from team member details
 * @returns Integrations with complete team member lists and selections
 */
export const getAvailableTeamMembersIntegrations = async (
  selectedIntegrations?: IntegrationInfoResponse[]
): Promise<TeamMemberIntegration[]> => {
  const endpoint = '/api/entity-integration-links/unlinked';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.INTEGRATIONS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: IntegrationLinksResponse[];
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        entityType: EntityType.User,
      },
    })
  );

  const unlinkedIntegrations = mapIntegrationLinksListFromDTO(response);

  const finalIntegrations = selectedIntegrations
    ? mergeIntegrations(unlinkedIntegrations, selectedIntegrations)
    : unlinkedIntegrations;

  return mapEntityIntegrationsToTeamMemberIntegrations(finalIntegrations).sort((a, b) =>
    a.type.localeCompare(b.type)
  );
};
